import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Loader2 } from "lucide-react";

import { DashboardPage } from "@/components/dashboard-page";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { QueryError } from "@/components/query-error";
import { useEventReportsListQuery } from "@/features/reports/api";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const LIST_DATE: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: ISTANBUL_TZ,
};

/**
 * Etkinlik Sonu Raporları — liste sayfası.
 *
 * Snapshot tablosundan organizer scope'lu kayıtlar; en yeni biten önce.
 * Her satır: event title + tarih + KPI özet → tıklayınca detay sayfasına.
 */
export function EventReportsPage() {
  return (
    <DashboardPage>
      <EventReportsView />
    </DashboardPage>
  );
}

const PAGE_SIZE = 25;

function EventReportsView() {
  const [page, setPage] = useState(1);
  const query = useEventReportsListQuery({ page, pageSize: PAGE_SIZE });
  const t = useT("eventReports");

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
      />

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : query.error ? (
        <QueryError
          message={t("loadError")}
          detail={query.error.detail}
          onRetry={() => query.refetch()}
          retrying={query.isFetching}
        />
      ) : !query.data || query.data.items.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={t("emptyTitle")}
          description={t("emptyDescription")}
        />
      ) : (
        <ReportsList items={query.data.items} />
      )}

      {query.data && query.data.totalCount > PAGE_SIZE ? (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={query.data.totalCount}
          onChange={setPage}
        />
      ) : null}
    </div>
  );
}

function ReportsList({
  items,
}: {
  items: NonNullable<
    ReturnType<typeof useEventReportsListQuery>["data"]
  >["items"];
}) {
  const t = useT("eventReports");
  const fmt = useFormat();
  const money = (value: number) => fmt.money(value, "TRY");
  return (
    <div className="overflow-hidden rounded-xl border">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-start font-medium">{t("columns.event")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.orders")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.sold")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.attendance")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.gross")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.net")}</th>
              <th className="px-4 py-3 text-end font-medium">{t("columns.refunds")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((row) => {
              const attendance =
                row.ticketsSold > 0 ? (row.ticketsCheckedIn / row.ticketsSold) * 100 : 0;
              return (
                <tr
                  key={row.snapshotId}
                  className="hover:bg-muted/30"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/reports/events/${row.eventId}`}
                      className="block font-medium hover:underline"
                    >
                      {row.eventTitle}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {fmt.date(row.eventEndsAt, LIST_DATE)} ·{" "}
                      {t("reportDate", { date: fmt.date(row.snapshotTakenAt, LIST_DATE) })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {fmt.number(row.orderCount)}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {fmt.number(row.ticketsSold)}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {fmt.number(row.ticketsCheckedIn)}{" "}
                    <span className="text-xs text-muted-foreground">
                      ({fmt.percent(attendance)})
                    </span>
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {money(row.grossRevenue)}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">
                    {money(row.organizerNetRevenue)}
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums text-muted-foreground">
                    {row.refundedAmount > 0 ? money(row.refundedAmount) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Pagination({
  page,
  pageSize,
  totalCount,
  onChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onChange: (page: number) => void;
}) {
  const t = useT("eventReports");
  const fmt = useFormat();
  const totalPages = Math.ceil(totalCount / pageSize);
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">
        {t("pagination", {
          page: fmt.number(page),
          pages: fmt.number(totalPages),
          total: fmt.number(totalCount),
        })}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t("previous")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          {t("next")}
        </Button>
      </div>
    </div>
  );
}
